import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { Observable } from 'rxjs';

import { EventTypeEnum } from '../../enums/event-type.enum';
import { EventsData, IBookedEventsByMonth, IMonthlyEventData } from '../../interfaces/event.interface';
import { ICustodyDTOIn } from '../../interfaces/garde.interface';
import { ILightUser } from '../../interfaces/users/user.interface';
import { DataExportService } from '../../services/data-export.service';
import {
    formatDateYYYYMMDD, getFirstDayOfSchoolYear,
    getLastDayOfSchoolYear
} from '../../utils/date.utils';
import {
    ExportData,
    ResetEventsData,
    SetCurrentEventType,
    SetEventsCurrentMonth,
    SetMealsEventCurrentMonth,
} from '../actions/events.action';
import { SetHomeworkEventCurrentMonth } from './../actions/events.action';
import { HomeworkSchoolState, HomeworkSchoolStateModel } from './homework-school.state';
import { MealsState, MealsStateModel } from './meals.state';
import { UsersState } from './users.state';

export interface EventsStateModel {
    currentEventType: EventTypeEnum;
    currentMonthEventsData: IMonthlyEventData;
    currentMonthBookedEvents: IBookedEventsByMonth;
    fullEventsData: IMonthlyEventData[];
    fullBookedEventsData: IBookedEventsByMonth[];
    currentMonthIndex: number;
}

@State<EventsStateModel>({
    name: 'events',
    defaults: {
        currentEventType: null,
        currentMonthEventsData: null,
        currentMonthBookedEvents: null,
        fullEventsData: [],
        fullBookedEventsData: [],
        currentMonthIndex: null,
    },
})
@Injectable()
export class EventsState {
    constructor(private store: Store, private dataExport: DataExportService) {}

    @Selector()
    public static getCurrentEventType(state: EventsStateModel): EventTypeEnum {
        return state.currentEventType;
    }

    @Selector()
    public static getCurrentMonthEventsData(state: EventsStateModel): IMonthlyEventData {
        return state.currentMonthEventsData;
    }

    @Selector()
    public static getCurrentMonthBookedEvents(state: EventsStateModel): IBookedEventsByMonth {
        return state.currentMonthBookedEvents;
    }

    @Selector()
    public static getFullEventsData(state: EventsStateModel): IMonthlyEventData[] {
        return state.fullEventsData;
    }

    @Selector()
    public static getFullBookedEventsData(state: EventsStateModel): IBookedEventsByMonth[] {
        return state.fullBookedEventsData;
    }

    @Selector([UsersState.getCustodies, MealsState, HomeworkSchoolState, UsersState.parentsForCustody])
    public static getData(
        state: EventsStateModel,
        custodies: ICustodyDTOIn,
        meals: MealsStateModel,
        homeworks: HomeworkSchoolStateModel,
        usersLight: ILightUser[]
    ): EventsData {
        const currentMonthIndex: number = state.currentMonthIndex;
        let eventsData: IMonthlyEventData;
        let bookedEvents: IBookedEventsByMonth;

        switch (state.currentEventType) {
            case EventTypeEnum.MEAL:
                eventsData = meals.mealsData?.find((data: IMonthlyEventData) => data.month === currentMonthIndex);
                bookedEvents = meals.bookedMeals?.find((data: IBookedEventsByMonth) => data.monthNumber === currentMonthIndex);
                break;

            case EventTypeEnum.HOMEWORK:
                eventsData = homeworks.homeworkSchoolData?.find((data: IMonthlyEventData) => data.month === currentMonthIndex);
                bookedEvents = homeworks.bookedHomeworkSchool?.find((data: IBookedEventsByMonth) => data.monthNumber === currentMonthIndex);
                break;
        }

        return {
            monthIndex: currentMonthIndex,
            monthsData: eventsData,
            monthsBookedData: bookedEvents,
            custody: custodies,
            parents: usersLight,
        };
    }

    @Action(SetCurrentEventType)
    public setCurrentEventType(ctx: StateContext<EventsStateModel>, action: SetCurrentEventType): EventsStateModel {
        return ctx.patchState({
            currentEventType: action.currentEventType,
        });
    }

    @Action(SetEventsCurrentMonth)
    public setEventsCurrentMonth(ctx: StateContext<EventsStateModel>, action: SetEventsCurrentMonth): EventsStateModel {
        let eventsData: IMonthlyEventData[];
        let bookedEvents: IBookedEventsByMonth[];

        switch (ctx.getState().currentEventType) {
            case EventTypeEnum.MEAL:
                eventsData = this.store.selectSnapshot(MealsState.mealsData);
                bookedEvents = this.store.selectSnapshot(MealsState.bookedMeals);
                break;

            case EventTypeEnum.HOMEWORK:
                eventsData = this.store.selectSnapshot(HomeworkSchoolState.homeworkSchoolData);
                bookedEvents = this.store.selectSnapshot(HomeworkSchoolState.bookedHomeworkSchool);
                break;
        }

        return ctx.patchState({
            currentMonthEventsData: eventsData?.find((monthEventsData: IMonthlyEventData) => monthEventsData.month === action.monthIndex),
            currentMonthBookedEvents: bookedEvents?.find(
                (monthBookedEvents: IBookedEventsByMonth) => monthBookedEvents.monthNumber === action.monthIndex
            ),
            fullEventsData: eventsData,
            fullBookedEventsData: bookedEvents,
            currentMonthIndex: action.monthIndex,
        });
    }

    @Action(SetMealsEventCurrentMonth)
    public setMealsEventCurrentMonth(ctx: StateContext<EventsStateModel>, action: SetMealsEventCurrentMonth): EventsStateModel {
        const eventsData: IMonthlyEventData[] = action.payload.eventsData;
        const bookedEvents: IBookedEventsByMonth[] = action.payload.bookedEvents;

        return ctx.patchState({
            currentMonthEventsData: eventsData?.find(
                (monthEventsData: IMonthlyEventData) => monthEventsData.month === action.payload.monthIndex
            ),
            currentMonthBookedEvents: bookedEvents?.find(
                (monthBookedEvents: IBookedEventsByMonth) => monthBookedEvents.monthNumber === action.payload.monthIndex
            ),
            fullEventsData: eventsData,
            fullBookedEventsData: bookedEvents,
            currentMonthIndex: action.payload.monthIndex,
        });
    }

    @Action(SetHomeworkEventCurrentMonth)
    public setHomeworkEventCurrentMonth(ctx: StateContext<EventsStateModel>, action: SetHomeworkEventCurrentMonth): EventsStateModel {
        const eventsData: IMonthlyEventData[] = action.payload.eventsData;
        const bookedEvents: IBookedEventsByMonth[] = action.payload.bookedEvents;

        return ctx.patchState({
            currentMonthEventsData: eventsData?.find(
                (monthEventsData: IMonthlyEventData) => monthEventsData.month === action.payload.monthIndex
            ),
            currentMonthBookedEvents: bookedEvents?.find(
                (monthBookedEvents: IBookedEventsByMonth) => monthBookedEvents.monthNumber === action.payload.monthIndex
            ),
            fullEventsData: eventsData,
            fullBookedEventsData: bookedEvents,
            currentMonthIndex: action.payload.monthIndex,
        });
    }

    @Action(ResetEventsData)
    public resetEventsData(ctx: StateContext<EventsStateModel>): EventsStateModel {
        return ctx.patchState({
            currentEventType: null,
            currentMonthEventsData: null,
            currentMonthBookedEvents: null,
            fullEventsData: [],
            currentMonthIndex: null,
        });
    }

    @Action(ExportData)
    public exportData(ctx: StateContext<EventsStateModel>, action: ExportData): Observable<void> {
        const startDate: string = action?.payload.startDate ?? formatDateYYYYMMDD(getFirstDayOfSchoolYear());
        const endDate: string = action?.payload.endDate ?? formatDateYYYYMMDD(getLastDayOfSchoolYear());
        return this.dataExport.exportData({ ...action.payload, startDate, endDate });
    }
}
